package br.com.vvrefeicoes.portal.feature.orgchart

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LoadErrorState
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.di.loadGuarded
import br.com.vvrefeicoes.portal.core.model.ScreenMode
import br.com.vvrefeicoes.portal.core.util.SystemDateTimeProvider
import br.com.vvrefeicoes.portal.domain.model.Company
import br.com.vvrefeicoes.portal.domain.model.CompanyLink
import br.com.vvrefeicoes.portal.domain.model.CompanyType
import br.com.vvrefeicoes.portal.domain.model.Director
import br.com.vvrefeicoes.portal.domain.model.Manager
import br.com.vvrefeicoes.portal.domain.model.OrgChartChange
import br.com.vvrefeicoes.portal.domain.model.OrgChartData
import br.com.vvrefeicoes.portal.domain.model.OrgChartStatus
import br.com.vvrefeicoes.portal.domain.model.OrgChartVersion
import br.com.vvrefeicoes.portal.domain.model.Supervisor
import br.com.vvrefeicoes.portal.domain.model.companiesOfManager
import br.com.vvrefeicoes.portal.domain.model.diffOrgChart
import br.com.vvrefeicoes.portal.domain.model.directCompaniesOfDirector
import br.com.vvrefeicoes.portal.domain.model.managersOfDirector
import br.com.vvrefeicoes.portal.domain.model.managersWithoutDirector
import br.com.vvrefeicoes.portal.domain.model.orgChartSortKey
import br.com.vvrefeicoes.portal.domain.model.orphanCompanies
import br.com.vvrefeicoes.portal.domain.model.totalsByType
import br.com.vvrefeicoes.portal.domain.repository.CompanyRepository
import br.com.vvrefeicoes.portal.domain.repository.OrgChartRepository
import br.com.vvrefeicoes.portal.domain.repository.UserCandidate
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.StringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.orgchart_supervisor_error
import portalvv.shared.generated.resources.orgchart_supervisor_saved
import portalvv.shared.generated.resources.orgchart_toast_draft_saved
import portalvv.shared.generated.resources.orgchart_toast_save_error

sealed interface OrgAction {
    data class TransferCompany(
        val directorId: String,
        val managerId: String?,
        val companyId: String,
    ) : OrgAction

    data class CopyCompany(
        val directorId: String,
        val managerId: String?,
        val companyId: String,
    ) : OrgAction

    data class TransferManager(
        val directorId: String,
        val managerId: String,
    ) : OrgAction

    data class CopyManager(
        val directorId: String,
        val managerId: String,
    ) : OrgAction

    data class UnlinkCompany(
        val directorId: String,
        val managerId: String,
        val companyId: String,
    ) : OrgAction

    data class UnlinkCompanyFromDirector(
        val directorId: String,
        val companyId: String,
    ) : OrgAction

    data class UnlinkManager(
        val directorId: String,
        val managerId: String,
    ) : OrgAction

    data class DeactivateCompany(
        val directorId: String,
        val managerId: String?,
        val companyId: String,
    ) : OrgAction

    data class DeactivateManager(
        val directorId: String,
        val managerId: String,
    ) : OrgAction

    data class DeactivateDirector(
        val directorId: String,
    ) : OrgAction

    data class AddManager(
        val directorId: String,
    ) : OrgAction

    data class AddCompany(
        val managerId: String?,
        val directorId: String?,
    ) : OrgAction

    data class ManageSupervisors(
        val companyId: String,
    ) : OrgAction
}

enum class OrgAddKind { Director, Manager, Company }

class ResponsibleOption(
    val managerId: String?,
    val directorId: String?,
    val managerName: String?,
    val directorName: String?,
)

class ManagerView(
    val manager: Manager,
    val companies: List<Company>,
)

class DirectorView(
    val director: Director,
    val managers: List<ManagerView>,
    val directCompanies: List<Company>,
    val counts: Map<CompanyType, Int>,
)

class OrgChartView(
    val directors: List<DirectorView>,
    val withoutDirectorManagers: List<ManagerView>,
    val withoutDirectorCompanies: List<Company>,
) {
    val isEmpty: Boolean
        get() = directors.isEmpty() && withoutDirectorManagers.isEmpty() && withoutDirectorCompanies.isEmpty()
}

class OrgChartController(
    private val repository: OrgChartRepository,
    private val companyRepository: CompanyRepository,
    private val scope: CoroutineScope,
    private val loadError: LoadErrorState = LoadErrorState(),
) {
    var loading by mutableStateOf(true)

    var mode by mutableStateOf(ScreenMode.Read)

    var versions by mutableStateOf<List<OrgChartVersion>>(emptyList())

    var currentVersion by mutableStateOf<OrgChartVersion?>(null)

    var selectedVersion by mutableStateOf<OrgChartVersion?>(null)

    var draft by mutableStateOf<OrgChartVersion?>(null)

    var currentData by mutableStateOf(OrgChartData())

    var directorCandidates by mutableStateOf<List<UserCandidate>>(emptyList())

    var managerCandidates by mutableStateOf<List<UserCandidate>>(emptyList())

    var companyCandidates by mutableStateOf<List<Company>>(emptyList())

    var canCreateVersion by mutableStateOf(true)

    var searchQuery by mutableStateOf("")

    var toastMessage by mutableStateOf<StringResource?>(null)

    var toastArgs by mutableStateOf<List<Any>>(emptyList())

    var pendingAction by mutableStateOf<OrgAction?>(null)

    var targetName by mutableStateOf("")

    var supervisorsCompanyId by mutableStateOf<String?>(null)

    var supervisorCandidates by mutableStateOf<List<UserCandidate>>(emptyList())

    var supervisorSearch by mutableStateOf("")

    private var supervisorsSnapshot: OrgChartData? = null

    var targetManagerId by mutableStateOf<String?>(null)

    var targetDirectorId by mutableStateOf<String?>(null)

    var includeCompanies by mutableStateOf(false)

    var selectedCompanyIds by mutableStateOf<Set<String>>(emptySet())

    var showVersionsDialog by mutableStateOf(false)

    var showNewVersionDialog by mutableStateOf(false)

    var showDiscardDialog by mutableStateOf(false)

    var showPublishDialog by mutableStateOf(false)

    var showChangeInfoDialog by mutableStateOf(false)

    var showPublishSuccess by mutableStateOf(false)

    var addKind by mutableStateOf<OrgAddKind?>(null)

    var addName by mutableStateOf("")

    var addManagerDirectorName by mutableStateOf("")

    var addCompanyManagerId by mutableStateOf<String?>(null)

    var addCompanyDirectorId by mutableStateOf<String?>(null)

    val isDrafting: Boolean
        get() = mode != ScreenMode.Read

    val hasDraft: Boolean
        get() = draft != null

    val displayedVersion: OrgChartVersion?
        get() = if (isDrafting) draft else selectedVersion ?: currentVersion

    val isCurrentVersionSelected: Boolean
        get() = selectedVersion?.status == OrgChartStatus.Current

    val view: OrgChartView
        get() {
            val query = searchQuery.trim()
            val directors =
                currentData.directors
                    .filter { !it.inactive }
                    .sortedBy { it.name.orgChartSortKey() }
                    .mapNotNull { directorView(it, query) }
            val (withoutDirectorManagers, withoutDirectorCompanies) = withoutDirectorView(query)
            return OrgChartView(directors, withoutDirectorManagers, withoutDirectorCompanies)
        }

    private fun directorView(
        director: Director,
        query: String,
    ): DirectorView? {
        val managers = currentData.managersOfDirector(director.id)
        val directCompanies = currentData.directCompaniesOfDirector(director.id)
        if (query.isEmpty() || director.name.contains(query, ignoreCase = true)) {
            val managerViews = managers.map { managerView(it, director.id, null) }.filterNotNull()
            val counts = countByType(managerViews.flatMap { it.companies } + directCompanies)
            return DirectorView(director, managerViews, directCompanies, counts)
        }
        val managerViews = managers.mapNotNull { managerView(it, director.id, query) }
        val filteredDirect = directCompanies.filter { it.name.contains(query, ignoreCase = true) }
        if (managerViews.isEmpty() && filteredDirect.isEmpty()) return null
        val counts = countByType(managerViews.flatMap { it.companies } + filteredDirect)
        return DirectorView(director, managerViews, filteredDirect, counts)
    }

    private fun managerView(
        manager: Manager,
        directorId: String,
        query: String?,
    ): ManagerView? {
        val companies = currentData.companiesOfManager(manager.id, directorId)
        if (query == null || manager.name.contains(query, ignoreCase = true)) {
            return ManagerView(manager, companies)
        }
        val filtered = companies.filter { it.name.contains(query, ignoreCase = true) }
        return if (filtered.isEmpty()) null else ManagerView(manager, filtered)
    }

    private fun withoutDirectorView(query: String): Pair<List<ManagerView>, List<Company>> {
        val filter = query.ifEmpty { null }
        val managers = currentData.managersWithoutDirector().mapNotNull { managerView(it, "", filter) }
        val orphans =
            currentData.orphanCompanies().filter {
                query.isEmpty() || it.name.contains(query, ignoreCase = true)
            }
        return managers to orphans
    }

    private fun countByType(companies: List<Company>): Map<CompanyType, Int> = companies.distinctBy { it.id }.groupingBy { it.type }.eachCount()

    val totals: Map<CompanyType, Int>
        get() = currentData.totalsByType()

    val changeLog: List<OrgChartChange>
        get() = if (!isDrafting) emptyList() else diffOrgChart(currentVersion?.data ?: OrgChartData(), currentData)

    val previousVersion: OrgChartVersion?
        get() {
            val current = displayedVersion ?: return null
            val index = versions.indexOfFirst { it.id == current.id }
            if (index < 0) return null
            return versions.getOrNull(index + 1)
        }

    val versionChangeLog: List<OrgChartChange>
        get() {
            if (isDrafting) return emptyList()
            val current = displayedVersion ?: return emptyList()
            val previous = previousVersion ?: return emptyList()
            return diffOrgChart(previous.data, current.data)
        }

    val allManagerNames: List<String>
        get() = currentData.managers.filter { !it.inactive }.map { managerDisplayName(it) }

    val allDirectorNames: List<String>
        get() = currentData.directors.filter { !it.inactive }.map { it.name }

    val responsibleCompanyOptions: List<ResponsibleOption>
        get() {
            val options = mutableListOf<ResponsibleOption>()
            val directors = currentData.directors.filter { !it.inactive }
            directors.forEach { director ->
                currentData.managersOfDirector(director.id).forEach { manager ->
                    options += ResponsibleOption(manager.id, director.id, manager.name, director.name)
                }
            }
            currentData.managersWithoutDirector().forEach { manager ->
                options += ResponsibleOption(manager.id, null, manager.name, null)
            }
            directors.forEach { director ->
                options += ResponsibleOption(null, director.id, null, director.name)
            }
            return options
        }

    val addDirectorOptions: List<String>
        get() = directorCandidates.map { it.name }.filterNot { name -> currentData.directors.any { it.name == name } }

    val addManagerOptions: List<String>
        get() = managerCandidates.map { it.name }.filterNot { name -> currentData.managers.any { it.name == name } }

    val addCompanyOptions: List<String>
        get() =
            companyCandidates
                .filter { !it.inactive }
                .map { it.name }
                .filterNot { name -> currentData.companies.any { it.name == name } }

    val addManagerDirectorOptions: List<String>
        get() = currentData.directors.filter { !it.inactive }.map { it.name }

    val isAddConfirmEnabled: Boolean
        get() =
            when (addKind) {
                null -> false
                OrgAddKind.Director -> addName.isNotBlank()
                OrgAddKind.Manager -> addName.isNotBlank()
                OrgAddKind.Company -> addName.isNotBlank() && (addCompanyManagerId != null || addCompanyDirectorId != null)
            }

    val isTargetResponsibleSelected: Boolean
        get() = targetManagerId != null || targetDirectorId != null

    fun init() {
        if (!loading) return
        scope.launch {
            loadError.loadGuarded {
                val snapshot = repository.loadInitial()
                versions = snapshot.versions
                currentVersion = snapshot.currentVersion
                selectedVersion = snapshot.currentVersion
                draft = snapshot.draft
                currentData = snapshot.currentVersion.data
            }
            loading = false
        }
        scope.launch {
            companyCandidates = runCatching { companyRepository.loadCompanies() }.getOrDefault(emptyList())
        }
    }

    val nextVersion: String
        get() {
            val period = SystemDateTimeProvider().currentPeriod()
            val prefix = "v${period.year.toString().padStart(4, '0')}.${period.month.toString().padStart(2, '0')}.r"
            val count = versions.count { it.number.startsWith(prefix) }
            return prefix + (count + 1).toString().padStart(2, '0')
        }

    fun startNewVersion() {
        showNewVersionDialog = true
    }

    fun startChangeInfo() {
        showChangeInfoDialog = true
    }

    fun dismissNewVersion() {
        showNewVersionDialog = false
    }

    fun confirmNewVersion() {
        scope.launch {
            try {
                val newDraft = repository.createDraft()
                draft = newDraft
                currentData = newDraft.data
                mode = ScreenMode.Create
                dismissNewVersion()
            } catch (throwable: Throwable) {
                toastMessage = Res.string.orgchart_toast_save_error
            }
        }
    }

    fun editDraft() {
        val existing = draft ?: return
        currentData = existing.data
        mode = ScreenMode.Edit
    }

    fun saveDraft() {
        val existing = draft ?: return
        val updated = existing.copy(data = currentData)
        scope.launch {
            try {
                repository.saveDraft(updated)
                draft = updated
                toastMessage = Res.string.orgchart_toast_draft_saved
            } catch (throwable: Throwable) {
                toastMessage = Res.string.orgchart_toast_save_error
            }
        }
    }

    fun backToRead() {
        currentData = currentVersion?.data ?: OrgChartData()
        selectedVersion = currentVersion
        mode = ScreenMode.Read
    }

    fun discardDraft() {
        val existing = draft ?: return
        scope.launch {
            try {
                repository.discardDraft(existing.id)
                draft = null
                currentData = currentVersion?.data ?: OrgChartData()
                selectedVersion = currentVersion
                mode = ScreenMode.Read
            } catch (throwable: Throwable) {
                toastMessage = Res.string.orgchart_toast_save_error
            }
        }
    }

    fun publishDraft(changes: List<String>) {
        val existing = draft ?: return
        val updated = existing.copy(data = currentData)
        scope.launch {
            try {
                val published = repository.publish(updated, changes)
                versions = repository.loadInitial().versions
                currentVersion = published
                selectedVersion = published
                draft = null
                currentData = published.data
                mode = ScreenMode.Read
                showPublishSuccess = true
            } catch (throwable: Throwable) {
                toastMessage = Res.string.orgchart_toast_save_error
            }
        }
    }

    fun dismissPublishSuccess() {
        showPublishSuccess = false
    }

    fun selectVersion(versionId: String) {
        val version = versions.firstOrNull { it.id == versionId } ?: return
        selectedVersion = version
        currentData = version.data
        showVersionsDialog = false
    }

    fun openAction(action: OrgAction) {
        when (action) {
            is OrgAction.AddManager -> {
                openAddManager(action.directorId)
                return
            }
            is OrgAction.AddCompany -> {
                openAddCompany(action.managerId, action.directorId)
                return
            }
            is OrgAction.ManageSupervisors -> {
                openSupervisors(action.companyId)
                return
            }
            else -> Unit
        }
        pendingAction = action
        targetName = ""
        targetManagerId = null
        targetDirectorId = null
        includeCompanies = false
        selectedCompanyIds =
            when (action) {
                is OrgAction.TransferManager -> companyIdsOfManager(action.managerId, action.directorId)
                is OrgAction.CopyManager -> companyIdsOfManager(action.managerId, action.directorId)
                else -> emptySet()
            }
    }

    fun companyIdsOfManager(
        managerId: String,
        directorId: String,
    ): Set<String> = currentData.companiesOfManager(managerId, directorId).map { it.id }.toSet()

    val pendingManagerCompanies: List<Company>
        get() {
            val (managerId, directorId) =
                when (val action = pendingAction) {
                    is OrgAction.TransferManager -> action.managerId to action.directorId
                    is OrgAction.CopyManager -> action.managerId to action.directorId
                    else -> return emptyList()
                }
            return currentData.companiesOfManager(managerId, directorId)
        }

    fun toggleCompanySelection(companyId: String) {
        selectedCompanyIds =
            if (companyId in selectedCompanyIds) {
                selectedCompanyIds - companyId
            } else {
                selectedCompanyIds + companyId
            }
    }

    fun toggleAllCompanies(checked: Boolean) {
        selectedCompanyIds = if (checked) pendingManagerCompanies.map { it.id }.toSet() else emptySet()
    }

    fun openAddDirector() {
        resetAdd()
        addKind = OrgAddKind.Director
        if (mode != ScreenMode.Read) {
            scope.launch {
                runCatching { repository.loadDirectorCandidates() }
                    .onSuccess { directorCandidates = it }
            }
        }
    }

    fun openAddManager(directorId: String? = null) {
        resetAdd()
        addKind = OrgAddKind.Manager
        addManagerDirectorName =
            directorId
                ?.let { id -> currentData.directors.firstOrNull { it.id == id }?.name }
                .orEmpty()
        if (mode != ScreenMode.Read) {
            scope.launch {
                runCatching { repository.loadManagerCandidates() }
                    .onSuccess { managerCandidates = it }
            }
        }
    }

    fun openAddCompany(
        managerId: String? = null,
        directorId: String? = null,
    ) {
        resetAdd()
        addKind = OrgAddKind.Company
        addCompanyManagerId = managerId
        addCompanyDirectorId =
            directorId
                ?: managerId?.let { id -> currentData.managerDirectors[id]?.firstOrNull() }
    }

    fun dismissAdd() {
        addKind = null
        resetAdd()
    }

    fun openSupervisors(companyId: String) {
        supervisorsCompanyId = companyId
        supervisorSearch = ""
        supervisorsSnapshot = currentData
        if (supervisorCandidates.isEmpty()) {
            scope.launch {
                runCatching { repository.loadSupervisorCandidates() }
                    .onSuccess { supervisorCandidates = it }
            }
        }
    }

    fun dismissSupervisors() {
        supervisorsSnapshot?.let { currentData = it }
        supervisorsCompanyId = null
        supervisorSearch = ""
        supervisorsSnapshot = null
    }

    /** Descarta as alterações do modal de supervisores e volta para a leitura. */
    fun cancelSupervisors() {
        supervisorsSnapshot?.let { currentData = it }
    }

    /**
     * Confirma as alterações do modal. Em rascunho, mantém no buffer (persistido depois pelo
     * "Salvar rascunho"); na versão vigente, persiste direto sem gerar nova versão.
     */
    fun saveSupervisors(companyId: String) {
        if (isDrafting) {
            supervisorsSnapshot = currentData
            return
        }
        val ids = currentData.unitSupervisors[companyId].orEmpty().toList()
        scope.launch {
            runCatching { repository.saveUnitSupervisors(companyId, ids) }
                .onSuccess {
                    // Volta para a leitura sem fechar o modal; o estado salvo vira o novo baseline.
                    supervisorsSnapshot = currentData
                    toastArgs =
                        listOf(
                            currentData.companies
                                .firstOrNull { it.id == companyId }
                                ?.name
                                .orEmpty(),
                        )
                    toastMessage = Res.string.orgchart_supervisor_saved
                }.onFailure { toastMessage = Res.string.orgchart_supervisor_error }
        }
    }

    /** Alterna o vínculo do supervisor na unidade (usa o candidato para nome/cargo). */
    fun toggleSupervisor(
        companyId: String,
        id: String,
    ) {
        val selected = currentData.unitSupervisors[companyId].orEmpty()
        if (id in selected) {
            removeSupervisor(companyId, id)
        } else {
            val candidate = supervisorCandidates.firstOrNull { it.id == id }
            addSupervisor(companyId, id, candidate?.name ?: id, candidate?.role.orEmpty())
        }
    }

    fun updateSupervisorSearch(value: String) {
        supervisorSearch = value
    }

    /** Candidatos ainda não vinculados à unidade atual. */
    val availableSupervisors: List<UserCandidate>
        get() {
            val assigned = supervisorsCompanyId?.let { currentData.unitSupervisors[it].orEmpty() } ?: emptySet()
            return supervisorCandidates.filter { it.id !in assigned }
        }

    fun supervisorsOf(companyId: String): List<Supervisor> {
        val ids = currentData.unitSupervisors[companyId].orEmpty()
        return currentData.supervisors.filter { it.id in ids }.sortedBy { it.name }
    }

    fun addSupervisor(
        companyId: String,
        id: String,
        name: String,
        cargo: String = "",
    ) {
        if (id.isBlank()) return
        val data = currentData
        val supervisors =
            if (data.supervisors.any { it.id == id }) {
                data.supervisors
            } else {
                data.supervisors + Supervisor(id, name.trim(), cargo = cargo)
            }
        val unitSupervisors = data.unitSupervisors + (companyId to ((data.unitSupervisors[companyId] ?: emptySet()) + id))
        currentData = data.copy(supervisors = supervisors, unitSupervisors = unitSupervisors)
    }

    fun removeSupervisor(
        companyId: String,
        id: String,
    ) {
        val data = currentData
        val unitSupervisors = data.unitSupervisors + (companyId to ((data.unitSupervisors[companyId] ?: emptySet()) - id))
        val referenced = unitSupervisors.values.flatten().toSet()
        currentData = data.copy(supervisors = data.supervisors.filter { it.id in referenced }, unitSupervisors = unitSupervisors)
    }

    fun selectAddCompanyResponsible(option: ResponsibleOption) {
        addCompanyManagerId = option.managerId
        addCompanyDirectorId = option.directorId
    }

    fun confirmAdd() {
        when (addKind) {
            OrgAddKind.Director -> {
                val candidate = directorCandidates.firstOrNull { it.name == addName }
                if (candidate != null) addDirector(candidate.id, addName)
            }
            OrgAddKind.Manager -> {
                val candidate = managerCandidates.firstOrNull { it.name == addName }
                if (candidate != null) addManager(candidate.id, addName, directorIdByName(addManagerDirectorName))
            }
            OrgAddKind.Company -> {
                val company = companyCandidates.firstOrNull { it.name == addName }
                if (company != null) addCompany(company.id, addName, company.type, addCompanyManagerId, addCompanyDirectorId)
            }
            null -> Unit
        }
        dismissAdd()
    }

    private fun resetAdd() {
        addName = ""
        addManagerDirectorName = ""
        addCompanyManagerId = null
        addCompanyDirectorId = null
    }

    fun addDirector(
        id: String,
        name: String,
    ) {
        val trimmed = name.trim()
        if (trimmed.isEmpty() || id.isBlank()) return
        val data = currentData
        currentData = data.copy(directors = data.directors + Director(id = id, name = trimmed))
    }

    fun addManager(
        id: String,
        name: String,
        directorId: String?,
    ) {
        val trimmed = name.trim()
        if (trimmed.isEmpty() || id.isBlank()) return
        val data = currentData
        currentData =
            data.copy(
                managers = data.managers + Manager(id = id, name = trimmed),
                managerDirectors =
                    if (directorId != null) {
                        data.managerDirectors + (id to setOf(directorId))
                    } else {
                        data.managerDirectors
                    },
            )
    }

    fun addCompany(
        id: String,
        name: String,
        type: CompanyType,
        managerId: String?,
        directorId: String?,
    ) {
        val trimmed = name.trim()
        if (trimmed.isEmpty() || id.isBlank()) return
        val data = currentData
        val link = CompanyLink(directorId.orEmpty(), managerId)
        currentData =
            data.copy(
                companies = data.companies + Company(id = id, name = trimmed, type = type),
                companyLinks = data.companyLinks + (id to setOf(link)),
            )
    }

    fun dismissAction() {
        pendingAction = null
        targetName = ""
        targetManagerId = null
        targetDirectorId = null
        includeCompanies = false
        selectedCompanyIds = emptySet()
    }

    fun selectTargetResponsible(option: ResponsibleOption) {
        targetManagerId = option.managerId
        targetDirectorId = option.directorId
    }

    fun confirmAction() {
        val action = pendingAction ?: return
        when (action) {
            is OrgAction.TransferCompany -> {
                val managerId = targetManagerId
                val directorId = targetDirectorId
                when {
                    managerId != null && directorId != null ->
                        if (action.managerId != null) {
                            transferCompany(action.companyId, action.managerId, action.directorId, managerId, directorId)
                        } else {
                            moveDirectCompany(action.companyId, managerId, directorId)
                        }
                    directorId != null -> transferCompanyToDirector(action.companyId, directorId)
                }
            }
            is OrgAction.CopyCompany -> {
                val managerId = targetManagerId
                val directorId = targetDirectorId
                when {
                    managerId != null && directorId != null -> copyCompany(action.companyId, managerId, directorId)
                    directorId != null -> copyCompanyToDirector(action.companyId, directorId)
                }
            }
            is OrgAction.TransferManager -> {
                val targetDirectorId = directorIdByName(targetName) ?: return
                transferManager(action.managerId, action.directorId, targetDirectorId, selectedCompanyIds)
            }
            is OrgAction.CopyManager -> {
                val targetDirectorId = directorIdByName(targetName) ?: return
                copyManager(action.managerId, targetDirectorId, selectedCompanyIds)
            }
            is OrgAction.UnlinkCompany -> unlinkCompany(action.companyId, action.managerId, action.directorId)
            is OrgAction.UnlinkCompanyFromDirector -> unlinkCompanyFromDirector(action.companyId, action.directorId)
            is OrgAction.UnlinkManager ->
                transferManager(
                    action.managerId,
                    action.directorId,
                    "",
                    if (includeCompanies) companyIdsOfManager(action.managerId, action.directorId) else emptySet(),
                )
            is OrgAction.DeactivateCompany -> deactivateCompany(action.companyId, action.managerId, action.directorId)
            is OrgAction.DeactivateManager -> deactivateManager(action.managerId, action.directorId)
            is OrgAction.DeactivateDirector -> deactivateDirector(action.directorId)
            is OrgAction.AddManager -> Unit
            is OrgAction.AddCompany -> Unit
            is OrgAction.ManageSupervisors -> Unit
        }
        dismissAction()
    }

    fun transferCompany(
        companyId: String,
        fromManagerId: String,
        fromDirectorId: String,
        toManagerId: String,
        toDirectorId: String,
    ) {
        val data = currentData
        val links =
            (data.companyLinks[companyId] ?: emptySet()) -
                CompanyLink(fromDirectorId, fromManagerId) +
                CompanyLink(toDirectorId, toManagerId)
        currentData = data.copy(companyLinks = data.companyLinks + (companyId to links))
    }

    fun copyCompany(
        companyId: String,
        toManagerId: String,
        toDirectorId: String,
    ) {
        val data = currentData
        currentData =
            data.copy(
                companyLinks =
                    data.companyLinks +
                        (companyId to ((data.companyLinks[companyId] ?: emptySet()) + CompanyLink(toDirectorId, toManagerId))),
            )
    }

    fun transferCompanyToDirector(
        companyId: String,
        directorId: String,
    ) {
        val data = currentData
        currentData = data.copy(companyLinks = data.companyLinks + (companyId to setOf(CompanyLink(directorId, null))))
    }

    fun copyCompanyToDirector(
        companyId: String,
        directorId: String,
    ) {
        val data = currentData
        currentData =
            data.copy(
                companyLinks =
                    data.companyLinks +
                        (companyId to ((data.companyLinks[companyId] ?: emptySet()) + CompanyLink(directorId, null))),
            )
    }

    fun unlinkCompany(
        companyId: String,
        managerId: String,
        directorId: String,
    ) {
        val data = currentData
        val links =
            (data.companyLinks[companyId] ?: emptySet()) - CompanyLink(directorId, managerId) + CompanyLink(directorId, null)
        currentData = data.copy(companyLinks = data.companyLinks + (companyId to links))
    }

    fun unlinkCompanyFromDirector(
        companyId: String,
        directorId: String,
    ) {
        val data = currentData
        val links = (data.companyLinks[companyId] ?: emptySet()).filterNot { it.directorId == directorId }.toSet()
        currentData =
            data.copy(
                companyLinks =
                    if (links.isEmpty()) data.companyLinks - companyId else data.companyLinks + (companyId to links),
            )
    }

    fun moveDirectCompany(
        companyId: String,
        toManagerId: String,
        toDirectorId: String,
    ) {
        val data = currentData
        val directLinks = (data.companyLinks[companyId] ?: emptySet()).filter { it.managerId == null }.toSet()
        currentData =
            data.copy(
                companyLinks =
                    data.companyLinks +
                        (companyId to ((data.companyLinks[companyId] ?: emptySet()) - directLinks + CompanyLink(toDirectorId, toManagerId))),
            )
    }

    fun deactivateCompany(
        companyId: String,
        managerId: String?,
        directorId: String?,
    ) {
        val data = currentData
        val target = CompanyLink(directorId.orEmpty(), managerId)
        val links = (data.companyLinks[companyId] ?: emptySet()) - target
        if (links.isEmpty()) {
            currentData =
                data.copy(
                    companies = data.companies.map { if (it.id == companyId) it.copy(inactive = true) else it },
                    companyLinks = data.companyLinks - companyId,
                )
        } else {
            currentData = data.copy(companyLinks = data.companyLinks + (companyId to links))
        }
    }

    fun transferManager(
        managerId: String,
        fromDirectorId: String,
        toDirectorId: String,
        companiesThatFollow: Set<String>,
    ) {
        if (fromDirectorId == toDirectorId) return
        val data = currentData
        val managerDirectorsUpdated = data.managerDirectors.toMutableMap()
        val withoutSource = (managerDirectorsUpdated[managerId] ?: emptySet()) - fromDirectorId
        managerDirectorsUpdated[managerId] =
            if (toDirectorId.isEmpty()) withoutSource else withoutSource + toDirectorId

        val companyLinksUpdated =
            data.companyLinks.mapValues { (companyId, links) ->
                val source = CompanyLink(fromDirectorId, managerId)
                if (source !in links) {
                    links
                } else {
                    val updatedLinks = links - source
                    when {
                        companyId in companiesThatFollow ->
                            if (toDirectorId.isEmpty()) updatedLinks else updatedLinks + CompanyLink(toDirectorId, managerId)
                        fromDirectorId.isEmpty() -> updatedLinks
                        else -> updatedLinks + CompanyLink(fromDirectorId, null)
                    }
                }
            }
        currentData =
            data.copy(
                managerDirectors = managerDirectorsUpdated,
                companyLinks = companyLinksUpdated,
            )
    }

    fun copyManager(
        managerId: String,
        toDirectorId: String,
        companiesThatFollow: Set<String>,
    ) {
        val data = currentData
        val managerDirectorsUpdated =
            data.managerDirectors + (managerId to ((data.managerDirectors[managerId] ?: emptySet()) + toDirectorId))
        val companyLinksUpdated = data.companyLinks.toMutableMap()
        companiesThatFollow.forEach { companyId ->
            companyLinksUpdated[companyId] =
                (companyLinksUpdated[companyId] ?: emptySet()) + CompanyLink(toDirectorId, managerId)
        }
        currentData =
            data.copy(
                managerDirectors = managerDirectorsUpdated,
                companyLinks = companyLinksUpdated,
            )
    }

    fun deactivateManager(
        managerId: String,
        directorId: String,
    ) {
        val data = currentData
        val remaining = (data.managerDirectors[managerId] ?: emptySet()) - directorId
        val companyLinksUpdated =
            data.companyLinks.mapValues { (_, links) ->
                val source = CompanyLink(directorId, managerId)
                when {
                    source !in links -> links
                    directorId.isEmpty() -> links - source
                    else -> (links - source) + CompanyLink(directorId, null)
                }
            }
        currentData =
            data.copy(
                managers =
                    if (remaining.isEmpty()) {
                        data.managers.map { if (it.id == managerId) it.copy(inactive = true) else it }
                    } else {
                        data.managers
                    },
                managerDirectors = data.managerDirectors + (managerId to remaining),
                companyLinks = companyLinksUpdated,
            )
    }

    fun deactivateDirector(directorId: String) {
        val data = currentData
        val companyLinksUpdated =
            data.companyLinks
                .map { (companyId, links) ->
                    companyId to
                        links
                            .mapNotNull { link ->
                                when {
                                    link.directorId != directorId -> link
                                    link.managerId != null -> CompanyLink("", link.managerId)
                                    else -> null
                                }
                            }.toSet()
                }.filter { (_, links) -> links.isNotEmpty() }
                .toMap()
        currentData =
            data.copy(
                directors = data.directors.map { if (it.id == directorId) it.copy(inactive = true) else it },
                managerDirectors = data.managerDirectors.mapValues { (_, directors) -> directors - directorId },
                companyLinks = companyLinksUpdated,
            )
    }

    private fun managerDisplayName(manager: Manager): String {
        val duplicated = currentData.managers.count { !it.inactive && it.name == manager.name } > 1
        if (!duplicated) return manager.name
        val directorId = currentData.managerDirectors[manager.id]?.firstOrNull()
        val directorName = currentData.directors.firstOrNull { it.id == directorId }?.name
        return if (directorName != null) "${manager.name} ($directorName)" else manager.name
    }

    private fun directorIdByName(name: String): String? = currentData.directors.firstOrNull { it.name == name }?.id
}

@Composable
fun rememberOrgChartController(): OrgChartController {
    val appGraph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    return remember { OrgChartController(appGraph.orgChartRepository, appGraph.companyRepository, scope, appGraph.loadError) }
}
